import { canonicalGalleryDomain } from '@/lib/templates/gallery-domain'
import { MAX_STREAMS_PER_COLLECTION } from './constants'
import { FLOW_KIND_SLUGS, type FlowKind } from './flow-kind'

/**
 * A flow's URL identity is its brand's canonical gallery domain
 * (`notion.com`, the key `templateBrands` and the gallery's `?brand=`
 * filter use) followed by the kind of send stream it shows:
 * `notion.com/onboarding`, `notion.com/promotions-2` for a brand's second
 * promotions stream. A flow published before streams existed is the bare
 * domain, and the bare domain is also the brand's URL: it redirects to the
 * brand's primary flow (`FLOW_KIND_PRIORITY`).
 *
 * Two TLDs are refused because the path would collide with reserved URL
 * suffixes: `.zip` sits in the proxy matcher's asset-extension exclusion
 * (`proxy.ts`), and `.md` is the Markdown-twin rewrite lane
 * (`lib/site/seo/markdown-paths.ts`).
 */
export const RESERVED_FLOW_SLUG_SUFFIXES: ReadonlyArray<string> = ['zip', 'md']

/** RFC 1035 hostname ceiling; anything longer is not a domain. */
const MAX_FLOW_SLUG_LENGTH = 253

export function hasReservedFlowSlugSuffix(domain: string): boolean {
  const suffix = domain.slice(domain.lastIndexOf('.') + 1)
  return RESERVED_FLOW_SLUG_SUFFIXES.includes(suffix)
}

/** The brand segment for a domain, or `''` when it cannot be a public flow URL. */
export function flowTemplateSlug(domain: string): string {
  const canonical = canonicalGalleryDomain(domain)
  if (!canonical || hasReservedFlowSlugSuffix(canonical)) {
    return ''
  }
  return canonical
}

/**
 * `<kind>` or `<kind>-<n>`, n from 2 to `MAX_STREAMS_PER_COLLECTION` (the
 * most streams of one kind a collection can hold, so every number the
 * publisher can mint parses).
 */
const STREAM_SEGMENT_PATTERN = /^([a-z][a-z-]*?)(?:-([2-9]|[1-9][0-9]{1,2}))?$/
const KIND_BY_SEGMENT = new Map<string, FlowKind>(
  Object.entries(FLOW_KIND_SLUGS).map(([kind, segment]) => [
    segment,
    kind as FlowKind,
  ])
)

/** `<kind segment>` or `<kind segment>-<n>` for a brand's n-th stream of that kind. */
export function flowStreamSegment(kind: FlowKind, ordinal = 1): string {
  const segment = FLOW_KIND_SLUGS[kind]
  return ordinal > 1 ? `${segment}-${ordinal}` : segment
}

/** The slug of a stream's flow, or `''` when the domain cannot be a flow URL. */
export function flowStreamTemplateSlug(
  domain: string,
  kind: FlowKind,
  ordinal = 1
): string {
  const brand = flowTemplateSlug(domain)
  return brand ? `${brand}/${flowStreamSegment(kind, ordinal)}` : ''
}

export type ParsedFlowTemplateSlug = {
  brandDomain: string
  /** Absent on a bare-domain slug. */
  kind?: FlowKind
  ordinal?: number
}

/** The parts of a stored or requested slug; `null` when it is not one. */
export function parseFlowTemplateSlug(
  value: unknown
): ParsedFlowTemplateSlug | null {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length > MAX_FLOW_SLUG_LENGTH
  ) {
    return null
  }
  const parts = value.split('/')
  if (parts.length > 2) {
    return null
  }
  const [domain = '', segment] = parts
  if (flowTemplateSlug(domain) !== domain) {
    return null
  }
  if (segment === undefined) {
    return { brandDomain: domain }
  }
  const match = STREAM_SEGMENT_PATTERN.exec(segment)
  const kind = match ? KIND_BY_SEGMENT.get(match[1] ?? '') : undefined
  if (!match || !kind) {
    return null
  }
  const ordinal = match[2] ? Number(match[2]) : 1
  if (ordinal > MAX_STREAMS_PER_COLLECTION) {
    return null
  }
  return { brandDomain: domain, kind, ordinal }
}

/** Guard for a slug that arrived from a URL or a request body. */
export function isValidFlowTemplateSlug(value: unknown): value is string {
  return parseFlowTemplateSlug(value) !== null
}

/** True for a stream flow's slug (`<domain>/<segment>`), false for a bare domain. */
export function isStreamFlowTemplateSlug(slug: string): boolean {
  return parseFlowTemplateSlug(slug)?.kind !== undefined
}

/** The signed-in flows gallery (rendered inside the app shell). */
export const FLOWS_PATH = '/flows'
/** The public mirror of the flows gallery: the crawlable canonical. */
export const PUBLIC_FLOWS_PATH = '/browse/flows'

/** `/flows` inside the app, `/browse/flows` on the public and marketing surfaces. */
export function flowsBasePathForSurface(
  surface: 'app' | 'public' | 'marketing'
): string {
  return surface === 'app' ? FLOWS_PATH : PUBLIC_FLOWS_PATH
}

/** `/flows/<slug>`, or the public mirror when `basePath` is `/browse/flows`. */
export function publicFlowTemplatePath(
  slug: string,
  basePath: string = FLOWS_PATH
): string {
  return `${basePath.replace(/\/$/, '')}/${slug}`
}

/** The flows gallery with an optional committed search query. */
export function buildFlowsUrl(options: {
  q?: string
  basePath?: string
}): string {
  const base = (options.basePath ?? FLOWS_PATH).replace(/\/$/, '')
  const query = options.q?.trim()
  if (!query) {
    return base
  }
  const params = new URLSearchParams()
  params.set('q', query)
  return `${base}?${params.toString()}`
}
